import { ref, computed } from 'vue'
import { useUIStore } from '@/stores/ui'
import { useSocialConnections } from '@/composables/useSocialConnections'
import apiInterceptor from '@/shared/apiInterceptor'
import { environment } from '@/shared/env'
import { channels } from '@/resources/js/bot-builder.js'

/**
 * Handles ALL count-related concerns for the Customers page.
 *
 * Key design rule: stats are independent of pagination.
 * - fetchTotalStats()   → call on mount and after mutations (delete, import)
 * - fetchFilteredStats() → call only when search/filter params change
 * Never call either of these in response to a page-number change.
 */
export function useCustomerStats() {
  const _uiStore = useUIStore(); const { connectedChannels } = useSocialConnections()

  const totalCustomers = ref(0)
  const filteredTotalCustomers = ref(0)
  const allChannels = ref(Object.values(channels).map(c => ({ ...c, count: 0 })))
  const channelWiseCustomers = computed(() => {
    const connected = connectedChannels.value
    return allChannels.value.filter(c => connected.includes(c.value))
  })
  
  const statsLoading = ref(false)
  let _filteredStatsRequestId = 0

  // ── Private ──────────────────────────────────────────────────────────────────

  async function _postCount(reqBody = {}) {
    return apiInterceptor.post(
      environment('VUE_APP_API_URL') + 'crm/v1/customers/count',
      reqBody,
    )
  }

  function _buildTargetingBody(searchState) {
    const {
      searchByFirstName: first_name = '',
      searchByLastName: last_name = '',
      searchByEmail: email = '',
      searchByMobile: mobile_number = '',
      searchByChannelText: channel = '',
      searchBySubscriptionStatus: subscription_status = '',
      searchByTagText: tags = '',
      segment: segmentid = '',
      searchByCustomFieldKey: name = '',
      searchByCustomFieldText: customFieldValue = '',
    } = searchState

    const reqBody = {}
    const conditions = {}

    if (first_name) {
      conditions.first_name = { operand: 'contains', value: first_name.toLowerCase().trim() }
    } else if (last_name) {
      conditions.last_name = { operand: 'contains', value: last_name.toLowerCase().trim() }
    } else if (email) {
      conditions.email = { operand: 'contains', value: email.toLowerCase().trim() }
    } else if (mobile_number) {
      conditions.mobile = { operand: 'contains', value: mobile_number }
    } else if (channel) {
      conditions.channel = channel.toLowerCase()
    } else if (subscription_status) {
      conditions.marketing_opt_in_state = { operand: 'in', value: [Number(subscription_status)] }
    } else if (tags) {
      conditions.tags = { operand: 'eq', value: tags }
    } else if (name && customFieldValue) {
      conditions.custom_fields = { operand: 'eq', value: `${name}:${customFieldValue}` }
    } else if (segmentid) {
      reqBody.segmentid = segmentid
    }

    if (Object.keys(conditions).length > 0) {
      reqBody.targeting_conditions = [conditions]
    }

    return reqBody
  }

  function _handleError(error) {
    _uiStore.updateToast({ open: true, message: error.message, type: 'danger' })
  }

  // ── Public ───────────────────────────────────────────────────────────────────

  /**
   * Fetches the global total customer count and channel breakdown.
   * No search filters applied. Call on mount and after mutations.
   */
  async function fetchTotalStats() {
    try {
      statsLoading.value = true
      const response = await _postCount()
      totalCustomers.value = response?.total ?? 0
      response?.channels && allChannels.value.forEach(c => c.count = response.channels[c.crmKey] ?? 0);
    } catch (error) {
      _handleError(error)
    } finally {
      statsLoading.value = false
    }
  }

  /**
   * Fetches the count matching the current search state.
   * Call only when search params change — NOT on pagination.
   * Channel counts are intentionally NOT updated here — they always
   * reflect the global total and are only set by fetchTotalStats().
   */
  async function fetchFilteredStats(searchState) {
    const requestId = ++_filteredStatsRequestId
    try {
      statsLoading.value = true
      filteredTotalCustomers.value = 0
      const reqBody = _buildTargetingBody(searchState)
      const response = await _postCount(reqBody)
      // Discard stale responses if a newer request was issued (e.g. filter cleared)
      if (requestId !== _filteredStatsRequestId) return
      filteredTotalCustomers.value = response?.total ?? 0
    } catch (error) {
      if (requestId === _filteredStatsRequestId) _handleError(error)
    } finally {
      if (requestId === _filteredStatsRequestId) statsLoading.value = false
    }
  }

  /** Directly set the filtered total (e.g. when clicking a channel stat card). */
  function setFilteredTotal(count) {
    filteredTotalCustomers.value = count
  }

  return {
    totalCustomers,
    filteredTotalCustomers,
    channelWiseCustomers,
    statsLoading,
    fetchTotalStats,
    fetchFilteredStats,
    setFilteredTotal,
  }
}
